import { z } from 'zod';
import { countsInConclusion } from './anomaly-effect.js';
import { stormwaterAnomalies } from './stormwater.js';
/**
 * `Inspection.result` — la conclusion du contrôle ([M3](../../../../docs/MODELE-DONNEES.md#m3)) :
 * un **fait constaté**, écrit une fois, à ne pas confondre avec `Inquiry.status`, qui dit où en
 * est la demande.
 *
 * Trois valeurs, imprimées telles quelles : GHP constate un raccordement, il ne rend pas d'avis de
 * conformité (réunion du 07/10/2026, M19).
 */
export const INSPECTION_RESULTS = ['compliant', 'partial', 'non_compliant'];
export const inspectionResultSchema = z.enum(INSPECTION_RESULTS);
/**
 * Ce que chaque conclusion **s'imprime** — sur un écran comme dans un fichier livré au client.
 *
 * ⚠️ **Des libellés, pas des identifiants** : ils sont en français par la troisième exception du
 * glossaire (« libellés imprimés »), et ce sont les mots de GHP — ceux du tableau de bord du
 * prototype validé —, pas une traduction inventée ici.
 *
 * ⚠️ Ils vivent dans le domaine parce que leurs lecteurs ne peuvent pas s'importer l'un l'autre :
 * le rapport et l'export client, composés par l'API, et les écrans des deux fronts.
 *
 * ⚠️ **Un seul vocabulaire, celui de GHP** : « Conforme / Non conforme », que l'éditeur du prototype
 * employait, est abandonné (réunion du 07/10/2026). La tablette, le bureau, le rapport et l'export
 * client lisent tous cette table.
 */
export const INSPECTION_RESULT_LABELS = {
    compliant: 'Raccordé',
    partial: 'Partiellement raccordé',
    non_compliant: 'Non raccordé',
};
/**
 * Les codes de la liste `technical` qui emportent la conclusion à eux seuls
 * ([M19](../../../../docs/MODELE-DONNEES.md#m19)).
 *
 * ⚠️ Des **codes**, pas des libellés : le libellé est du texte client, reformulable sans migrer
 * une donnée (M17). Écrire la règle sur « Non raccordé » la casserait au premier renommage
 * depuis « Administration → Règles anomalies ».
 */
export const HARD_TECHNICAL_CODES = ['NON_RACCORDE', 'REFUS'];
/**
 * La conclusion **dérivée**, avec priorité — fonction pure reprise du prototype validé et
 * spécifiée en [M19](../../../../docs/MODELE-DONNEES.md#m19) :
 *
 * ```
 * un élément technique dur (technical/NON_RACCORDE, technical/REFUS) → non_compliant
 * sinon, au moins une anomalie                                       → partial
 * sinon                                                              → compliant
 * ```
 *
 * ⚠️ C'est un **défaut juste, pas une contrainte** : M19 veut la conclusion surchargeable à la
 * main, et ne durcit pas la surcharge. Un appelant qui affiche cette valeur doit donc laisser
 * la place à celle que l'agent aura pu poser — la dérivation ne décide qu'en son absence.
 *
 * L'argument n'est pas typé `SubmittedAnomaly` à dessein. La règle ne lit qu'un code technique,
 * et `SubmittedAnomaly` vit dans `sync/`, qui importe déjà `inspection/` : le typer ainsi
 * créerait un cycle pour une dépendance dont la règle n'a pas besoin. La forme minimale
 * accepte aussi bien la proposition terrain que la ligne `anomaly` en base.
 */
export function deriveInspectionResult(anomalies, survey) {
    const hard = HARD_TECHNICAL_CODES;
    const counted = anomalies.filter(countsInConclusion);
    if (counted.some((anomaly) => anomaly.technical != null && hard.includes(anomaly.technical))) {
        return 'non_compliant';
    }
    const stormwater = survey === undefined ? [] : stormwaterAnomalies(survey);
    return counted.length + stormwater.filter(countsInConclusion).length > 0 ? 'partial' : 'compliant';
}
/**
 * **Combien d'anomalies le contrôle relève** — ses lignes, plus la pluie envoyée aux eaux usées,
 * qui en est une sans avoir de ligne (GHP DIGIT, RG-F07), selon le réseau et la règle du client
 * (`stormwaterAnomalies`). La transmission, le bureau, l'export et le rapport affichent ce compte
 * à côté de la liste des lignes : il les compte **toutes**, effet du client compris (#413) — seule
 * la conclusion écarte celles qui ne comptent pas.
 */
export function recordedAnomalyCount(inspection) {
    const stormwater = inspection.survey === undefined ? 0 : stormwaterAnomalies(inspection.survey).length;
    return inspection.anomalies.length + stormwater;
}
/**
 * Arbitre entre la conclusion **posée à la main** et la conclusion **dérivée**
 * ([M19](../../../../docs/MODELE-DONNEES.md#m19)) : la surcharge gagne, l'absence de surcharge
 * laisse la règle décider.
 *
 * ⚠️ **La dérivation reste vivante**, et c'est tout l'intérêt de ne rien figer à la saisie :
 * tant que l'agent n'a pas tranché, une anomalie ajoutée cinq minutes plus tard déplace la
 * conclusion. Une valeur écrite à l'ouverture de la fiche aurait gelé le constat du moment où
 * l'écran s'est affiché, c'est-à-dire avant le contrôle.
 *
 * ⚠️ **Quatre lecteurs**, et c'est pourquoi la règle vit ici : l'onglet « Contrôle » de la
 * tablette, la fiche de lecture du bureau, le fichier livré au client (M26) et le rapport à
 * venir (M11). Un `result ?? derive(...)` recopié quatre fois, c'est un fichier client qui
 * cesse de dire ce que l'écran affichait au premier oubli.
 */
export function resolveInspectionResult(inspection) {
    const derived = deriveInspectionResult(inspection.anomalies, inspection.survey);
    return {
        value: inspection.result ?? derived,
        derived,
        overridden: inspection.result != null,
    };
}
/** Du constat le plus favorable au plus défavorable — l'ordre dans lequel une conclusion monte. */
const RESULT_GRAVITY = {
    compliant: 0,
    partial: 1,
    non_compliant: 2,
};
/**
 * **La conclusion après l'enregistrement d'anomalies**, telle que le prototype validé l'écrit
 * (`applyAutoConclusion`) : une anomalie relevée fait passer le contrôle en « partiellement
 * raccordé », un élément technique dur le fait passer en « non raccordé » — et **ça écrase**.
 *
 * ⚠️ **Ce que `resolveInspectionResult` ne fait pas, et pourquoi il faut les deux.** La dérivation
 * se recalcule à chaque rendu, mais elle **cède devant une surcharge** (M19) : un agent qui avait
 * conclu « Raccordé » avant d'aller voir la cave gardait cette conclusion, et l'anomalie qu'il
 * venait de relever ne la déplaçait plus. Le prototype, lui, réécrit la conclusion **dès qu'une
 * anomalie est enregistrée** (`applyAutoConclusion`), quel que soit le geste qui l'enregistre — le
 * lier à un seul chemin de saisie laisse partir la conclusion d'avant par tous les autres. Cette
 * fonction est ce moment-là ; c'est à son appelant de n'en avoir qu'un.
 *
 * Elle rend **ce qu'il faut stocker dans `result`**, et l'escalade se fait en rendant la main à la
 * règle (`null`) plutôt qu'en écrivant la valeur calculée : écrire `partial` marquerait la
 * conclusion « posée à la main », alors que personne ne l'a posée — l'agent perdrait le bouton de
 * retour à la déduction, et le bureau lirait un choix là où il n'y a qu'un calcul.
 *
 * ⚠️ **Une surcharge qui *aggrave* survit**, et c'est le `c.conclusion !== 'bad'` du prototype :
 * l'agent qui a conclu « Non raccordé » de son propre chef ne voit pas son constat ramené à
 * « partiellement raccordé » parce qu'une anomalie bénigne s'ajoute. Seule une surcharge qui
 * **minore** ce que le relevé établit est rendue à la règle.
 */
export function concludeAfterAnomalies(inspection) {
    const { value, derived } = resolveInspectionResult(inspection);
    if (RESULT_GRAVITY[value] < RESULT_GRAVITY[derived])
        return null;
    return inspection.result ?? null;
}
//# sourceMappingURL=result.js.map